// The `image` widget: a URL and the alt text that goes with it.
//
// The authoring side of the `image` param type, which the port dropped —
// common/schema/types/params/utils recorded the reason as "needs a file service
// pagews does not have". It has one now.
//
// WHY THE VALUE IS AN OBJECT AND NOT A URL
//
// Because the second field is not decoration. A section declaring an `image`
// slot is declaring a picture that will end up in an `<img>` on a page, and an
// `<img>` with no alt is announced by a screen reader as its filename — which
// here is a uuid. Splitting it also means the template reads the two
// separately: `<img src="<%= hero.url %>" alt="<%= hero.description %>">`,
// which is the shape gen-2's templates were written against.
//
// The description is SEEDED from the file's own when one is picked, and
// editable afterwards. The file's description belongs to the file and is the
// same on every page it appears on; the alt text belongs to the place it is
// used. Seeding rather than reading through is what lets both be true.
//
// The shape is declared in two places and they have to agree — here it is only
// read, but see `/Types/Image` in common/schema/types/params/types/image and the
// `image` branch of @repoflow/components/types-values, which is the same object
// generated for a section's Values form.
import React, { useId, useState } from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import * as FileSelector from "components/file-selector";
import { IMAGES } from "@common/schema/site-file/kind";

export const View = ({ value }) => {
  const { url, description } = value || {};

  if (!url) {
    return <span className="text-muted">No image</span>;
  }

  return <FileSelector.Preview url={url} alt={description || ""} />;
};

export const Edit = (props) => {
  const {
    value,
    setValue,
    field: { property = {}, config = {} } = {},
    issues = [],
    labelId,
    describedById,
  } = props;

  const [touched, setTouched] = useState(false);
  const readOnly = Boolean(config.readOnly || property.readOnly);

  // NAMING TWO CONTROLS FROM ONE LABEL
  //
  // The object widget hands in one `labelId` — the <span> in the <dt> holding
  // the field's label — and this widget renders two text controls under it.
  // Pointing both at that one id would give two controls the same name, which
  // is the ambiguity a locator fails on and a screen reader cannot resolve.
  // So each control is named by the field's label AND its own sub-label, in
  // that order: `aria-labelledby` concatenates in the order given, so a field
  // called "Hero" announces as "Hero Url" and "Hero Description", and
  // `getByLabelText("Hero Url")` finds exactly one of them.
  //
  // The sub-labels are real <label>s pointed at their inputs too, which is
  // what makes clicking one focus its control; the ids they carry are what the
  // concatenation reads. useId rather than the property name, which repeats
  // across list rows.
  //
  // Only the ids that exist: `labelId` is undefined in a table cell, where the
  // column's <th> names the cell, and "undefined" is not an id.
  const ownId = useId();
  const urlLabelId = `${ownId}-url-label`;
  const urlInputId = `${ownId}-url`;
  const altLabelId = `${ownId}-alt-label`;
  const altInputId = `${ownId}-alt`;
  const namedBy = (subLabelId) => [labelId, subLabelId].filter(Boolean).join(" ");

  // A `default` the schema did not supply, or a row written before this param
  // type existed. Rendering the two inputs over an empty object is right;
  // gen-2's "Invalid value" for a falsy value left the field unusable with
  // nothing to click.
  const { url = "", description = "" } = value || {};

  const write = (mod) => {
    setValue({ url, description, ...mod });
    setTouched(true);
  };

  return (
    <Row>
      <Col md={8}>
        <FormGroup>
          <Label id={urlLabelId} for={urlInputId}>
            Url
          </Label>
          <div className="input-group">
            <Input
              id={urlInputId}
              type="text"
              aria-labelledby={namedBy(urlLabelId)}
              aria-describedby={describedById}
              readOnly={readOnly}
              value={url}
              invalid={touched && issues.length !== 0}
              onChange={(e) => write({ url: e.target.value })}
            />
            {!readOnly && (
              <div className="input-group-append">
                <FileSelector.SelectButton
                  kind={config.fileKind || property.fileKind || IMAGES}
                  onSelect={(picked, file) =>
                    // Both at once, and through one write. Two setValue calls
                    // would be two snapshot merges onto the same pre-batch
                    // object and the second would win outright — the shape
                    // src/forms/write.js exists for, one level up.
                    write({
                      url: picked,
                      // Only when there is nothing to lose. Somebody who has
                      // written alt text and then swaps the picture keeps their
                      // words; an empty field is filled in from the file.
                      description: description || (file && file.description) || "",
                    })
                  }
                />
              </div>
            )}
          </div>
        </FormGroup>

        <FormGroup>
          <Label id={altLabelId} for={altInputId}>
            Description
          </Label>
          <Input
            id={altInputId}
            type="textarea"
            aria-labelledby={namedBy(altLabelId)}
            aria-describedby={describedById}
            readOnly={readOnly}
            value={description}
            onChange={(e) => write({ description: e.target.value })}
          />
        </FormGroup>
      </Col>

      <Col md={4}>
        <FileSelector.Preview url={url} alt={description} />
      </Col>
    </Row>
  );
};
